import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  PageToolbar,
  SectionTitle,
  EmptyState,
  ToolbarDivider,
} from '../../components/ui/PageLayout';
import {
  AnimatedTabIndicator,
  animatedTabTriggerClass,
} from '../../components/ui/AnimatedTabIndicator';
import DeploymentFrequencyDoraCard from '../../components/dora/DeploymentFrequencyDoraCard';
import LeadTimeDoraCard from '../../components/dora/LeadTimeDoraCard';
import ChurnRatioCard from '../../components/dora/ChurnRatioCard';
import CycleTimeSection from '../../components/dora/CycleTimeSection';
import CodeCompositionCard from '../../components/dora/CodeCompositionCard';
import CodeCompositionOverTimeCard from '../../components/dora/CodeCompositionOverTimeCard';
import { Waves, FolderKanban, Users, FolderGit2, BarChart3, GitFork, GitCommit, GitPullRequest, Rocket, Clock } from 'lucide-react';
import { PageToolbarSkeleton, CardGridSkeleton } from '../../components/ui/Skeleton';
import SearchBar from '../../components/ui/SearchBar';
import AddMemberModal from './AddMemberModal';
import CommitHeatmap from '../../components/CommitHeatmap/CommitHeatmap';
import ContributorsBarChartCard from '../../components/contributors/ContributorsBarChartCard';
import { FeatureGate, useFeatureFlag } from '../../contexts/FeatureFlagContext';
import OrganisationStreamCard from '../../components/streams/OrganisationStreamCard';
import OrganisationProjectCard from '../../components/projects/OrganisationProjectCard';
import { ORGANISATION_STREAMS_CHANGED } from '../../constants/orgDashboardEvents';
import { useResolvedOrgId } from '../../hooks/useResolvedOrgId';
import { useDashboardRemoteData } from './hooks/useDashboardRemoteData';
import DashboardGettingStarted from './DashboardGettingStarted';
import DashboardCommitsTab from './tabs/DashboardCommitsTab';
import DashboardPullRequestsTab from './tabs/DashboardPullRequestsTab';
import { KpiCard } from '../../components/ui/KpiCard';
import RepositoriesConnectedTable from '../../components/repositories/RepositoriesConnectedTable';
import { repositoryNameAndOwner } from '../../components/repositories/repositoriesPageUtils';
import { formatCompactNumber } from '../../utils/formatNumber';

/** Buckets a raw daily series into N averaged points for a sparkline, with %-change vs window start. */
function toSparklineAndDelta(values: number[], buckets = 5) {
  if (!Array.isArray(values) || values.length < 2) return { sparkline: null, deltaPct: null };
  const chunkSize = Math.max(1, Math.ceil(values.length / buckets));
  const sparkline: number[] = [];
  for (let i = 0; i < values.length; i += chunkSize) {
    const chunk = values.slice(i, i + chunkSize);
    sparkline.push(chunk.reduce((a, b) => a + b, 0) / chunk.length);
  }
  const third = Math.max(1, Math.floor(sparkline.length / 3));
  const recentAvg = sparkline.slice(-third).reduce((a, b) => a + b, 0) / third;
  const priorAvg = sparkline.slice(0, third).reduce((a, b) => a + b, 0) / third;
  if (priorAvg === 0) return { sparkline, deltaPct: null };
  const deltaPct = Math.round(((recentAvg - priorAvg) / priorAvg) * 100);
  return { sparkline, deltaPct };
}

const Dashboard = () => {
  const navigate = useNavigate();
  const showContributorsTab = useFeatureFlag('ui.dashboard.contributorsBarChart', false);
  const showCommitsTab = useFeatureFlag('ui.dashboard.commitsTab', false);
  const showPullRequestsTab = useFeatureFlag('ui.dashboard.pullRequestsTab', false);
  const useBatchMetrics = useFeatureFlag('api.organisations.dashboardMetrics.read', false);
  const { orgId, ready } = useResolvedOrgId();

  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [deploymentPeriod, setDeploymentPeriod] = useState('yearly');
  const [leadTimePeriod, setLeadTimePeriod] = useState('yearly');
  const [doraChartType, setDoraChartType] = useState('area');
  const [leadTimeChartType, setLeadTimeChartType] = useState('area');
  const [churnPeriod, setChurnPeriod] = useState('yearly');
  const [churnChartType, setChurnChartType] = useState('area');
  const [contributorsTimeframe, setContributorsTimeframe] = useState('week');
  const [contributorsStatus, setContributorsStatus] = useState('active');
  const [activeDashboardTab, setActiveDashboardTab] = useState('overview');
  const [projectsSearch, setProjectsSearch] = useState('');
  const [reposSearch, setReposSearch] = useState('');
  const [contributorsSearch, setContributorsSearch] = useState('');

  const deploymentWindowDays =
    deploymentPeriod === 'weekly' ? 7 : deploymentPeriod === 'yearly' ? 365 : 30;
  const leadTimeWindowDays =
    leadTimePeriod === 'weekly' ? 7 : leadTimePeriod === 'yearly' ? 365 : 30;
  const churnWindowDays = churnPeriod === 'weekly' ? 7 : churnPeriod === 'yearly' ? 365 : 30;

  const {
    stats,
    orgName,
    statsQuery,
    profileQuery,
    dashboardStreamsLoading,
    dashboardStreams,
    dashboardProjects,
    dashboardProjectsLoading,
    dashboardRepos,
    dashboardReposLoading,
    deploymentDora,
    deploymentDoraMeta,
    deploymentLoading,
    leadTimeDora,
    leadTimeDoraMeta,
    leadTimeLoading,
    churnData,
    churnLoading,
    contributorsBarRows,
    contributorsBarLoading,
    invalidateAfterStructureChange,
    invalidateStatsOnly,
  } = useDashboardRemoteData({
    orgId,
    activeDashboardTab,
    deploymentWindowDays,
    leadTimeWindowDays,
    churnWindowDays,
    contributorsTimeframe,
    contributorsStatus,
    showContributorsTab,
    useBatchMetrics,
  });

  const initialLoading = !ready || (!!orgId && statsQuery.isPending);

  useEffect(() => {
    const onOrgStreamsChanged = () => {
      if (orgId) invalidateAfterStructureChange();
    };
    window.addEventListener(ORGANISATION_STREAMS_CHANGED, onOrgStreamsChanged);
    return () => window.removeEventListener(ORGANISATION_STREAMS_CHANGED, onOrgStreamsChanged);
  }, [orgId, invalidateAfterStructureChange]);

  useEffect(() => {
    setActiveDashboardTab('overview');
  }, [orgId]);

  useEffect(() => {
    if (!showContributorsTab && activeDashboardTab === 'contributors') {
      setActiveDashboardTab('overview');
    }
    if (!showCommitsTab && activeDashboardTab === 'commits') {
      setActiveDashboardTab('overview');
    }
    if (!showPullRequestsTab && activeDashboardTab === 'pull-requests') {
      setActiveDashboardTab('overview');
    }
  }, [showContributorsTab, showCommitsTab, showPullRequestsTab, activeDashboardTab]);

  const dashboardTabs = useMemo(() => {
    const streamN = stats?.streamCount ?? 0;
    const projectN = stats?.projectCount ?? 0;
    const repoN = stats?.repositoryCount ?? 0;
    const contributorN = (contributorsBarRows ?? []).length;
    const tabs = [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'reports', label: 'Reports', icon: BarChart3 },
    ];
    tabs.push(
      { id: 'streams', label: `Streams (${streamN})`, icon: Waves },
      { id: 'projects', label: `Projects (${projectN})`, icon: FolderKanban },
      { id: 'repos', label: `Repos (${repoN})`, icon: FolderGit2 }
    );
    if (showContributorsTab) {
      tabs.push({ id: 'contributors', label: `Contributors (${contributorN})`, icon: Users });
    }
    if (showPullRequestsTab) {
      tabs.push({ id: 'pull-requests', label: 'Pull requests', icon: GitPullRequest });
    }
    if (showCommitsTab) {
      tabs.push({ id: 'commits', label: 'Commits', icon: GitCommit });
    }
    return tabs;
  }, [showContributorsTab, showCommitsTab, showPullRequestsTab, stats, contributorsBarRows]);

  const onDashboardTabListKeyDown = useCallback(
    (e) => {
      const ids = dashboardTabs.map((t) => t.id);
      const idx = ids.indexOf(activeDashboardTab);
      if (idx < 0) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveDashboardTab(ids[(idx + 1) % ids.length]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveDashboardTab(ids[(idx - 1 + ids.length) % ids.length]);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveDashboardTab(ids[0]);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveDashboardTab(ids[ids.length - 1]);
      }
    },
    [dashboardTabs, activeDashboardTab]
  );

  const filteredDashboardProjects = useMemo(() => {
    const q = (projectsSearch || '').trim().toLowerCase();
    if (!q) return dashboardProjects;
    return dashboardProjects.filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.streamName && p.streamName.toLowerCase().includes(q))
    );
  }, [dashboardProjects, projectsSearch]);

  const filteredDashboardRepos = useMemo(() => {
    const q = (reposSearch || '').trim().toLowerCase();
    if (!q) return dashboardRepos;
    return dashboardRepos.filter((repo) => {
      const { name, owner, fullDisplay } = repositoryNameAndOwner(repo);
      return (
        fullDisplay.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q) ||
        owner.toLowerCase().includes(q) ||
        ((repo as any).streamName && (repo as any).streamName.toLowerCase().includes(q)) ||
        ((repo as any).projectName && (repo as any).projectName.toLowerCase().includes(q))
      );
    });
  }, [dashboardRepos, reposSearch]);

  const hasReposSearch = Boolean((reposSearch || '').trim());

  const deploymentFreqPerDay = useMemo(() => {
    const total = deploymentDora?.deploymentFrequency;
    if (total == null || !Number.isFinite(total) || deploymentWindowDays <= 0) return null;
    return Math.round((total / deploymentWindowDays) * 10) / 10;
  }, [deploymentDora?.deploymentFrequency, deploymentWindowDays]);

  const meanLeadTimeHoursLabel = useMemo(() => {
    const minutes = leadTimeDoraMeta?.meanLeadTimeMinutes;
    if (minutes == null || !Number.isFinite(minutes)) return null;
    return `${Math.round((minutes / 60) * 10) / 10}h`;
  }, [leadTimeDoraMeta?.meanLeadTimeMinutes]);

  const deploymentSpark = useMemo(
    () => toSparklineAndDelta(deploymentDoraMeta?.mergeDeploymentsByDay?.counts ?? []),
    [deploymentDoraMeta?.mergeDeploymentsByDay?.counts]
  );

  const leadTimeSpark = useMemo(
    () => toSparklineAndDelta(leadTimeDoraMeta?.leadTimeByDeployment?.leadTimeMinutes ?? []),
    [leadTimeDoraMeta?.leadTimeByDeployment?.leadTimeMinutes]
  );

  const activeContributorsValue = contributorsBarRows?.length ?? stats?.memberCount ?? null;

  const contributorsSummary = useMemo(() => {
    const rows = contributorsBarRows ?? [];
    const totalCommits = rows.reduce((s, r) => s + (Number(r.totalCommits) || 0), 0);
    const totalPRs = rows.reduce((s, r) => s + (Number(r.pullRequests) || 0), 0);
    const netLines = rows.reduce(
      (s, r) => s + (Number(r.linesAdded) || 0) - (Number(r.linesDeleted) || 0),
      0
    );
    const mostActive = rows.reduce< (typeof rows)[number] | null >(
      (top, r) => (top && Number(top.totalCommits) >= Number(r.totalCommits) ? top : r),
      null
    );
    return {
      totalCommits,
      totalPRs,
      netLinesLabel: `${netLines >= 0 ? '+' : ''}${formatCompactNumber(netLines)}`,
      mostActiveName: mostActive?.displayName ?? null,
    };
  }, [contributorsBarRows]);

  const filteredContributorsBarRows = useMemo(() => {
    const q = (contributorsSearch || '').trim().toLowerCase();
    if (!q) return contributorsBarRows;
    return contributorsBarRows.filter(
      (r) =>
        r.displayName?.toLowerCase().includes(q) || r.displayEmail?.toLowerCase().includes(q)
    );
  }, [contributorsBarRows, contributorsSearch]);

  if (initialLoading) {
    return (
      <PageContainer className="dashboard-org-tight">
        <PageToolbarSkeleton />
        <div className="border-t border-dark-border my-4" />
        <CardGridSkeleton count={3} />
      </PageContainer>
    );
  }

  const orgProfile = profileQuery.data;
  const hasGitHubIntegration =
    orgProfile?.hasToken === true || orgProfile?.hasGitHubIntegration === true;

  return (
    <PageContainer className="dashboard-org-tight">
      <PageToolbar title={orgName || 'Organisation'} />
      <ToolbarDivider />

      <DashboardGettingStarted
        orgId={orgId}
        stats={stats}
        hasGitHubIntegration={hasGitHubIntegration}
      />

      <div className="border-b border-dark-border mb-6">
        <nav
          className="flex gap-1 -mb-px overflow-x-auto"
          role="tablist"
          aria-label="Organisation dashboard sections"
          onKeyDown={onDashboardTabListKeyDown}
        >
          {dashboardTabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`dashboard-tab-${id}`}
              aria-selected={activeDashboardTab === id}
              aria-controls={`dashboard-tabpanel-${id}`}
              tabIndex={activeDashboardTab === id ? 0 : -1}
              onClick={() => setActiveDashboardTab(id)}
              className={animatedTabTriggerClass(activeDashboardTab === id)}
            >
              <AnimatedTabIndicator
                layoutId="dashboard-tab-indicator"
                isActive={activeDashboardTab === id}
              />
              <span className="relative z-10 inline-flex items-center gap-2">
                <Icon className="w-4 h-4" aria-hidden />
                {label}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {activeDashboardTab === 'overview' && (
        <div
          role="tabpanel"
          id="dashboard-tabpanel-overview"
          aria-labelledby="dashboard-tab-overview"
        >
          <FeatureGate flag="ui.dashboard.doraSection">
            <div className="card-grid-3-lg-mdl gap-4 mb-6">
              <KpiCard
                label="Deployment frequency"
                icon={Rocket}
                sparkline={deploymentSpark.sparkline}
                delta={
                  deploymentSpark.deltaPct != null
                    ? `${deploymentSpark.deltaPct > 0 ? '+' : ''}${deploymentSpark.deltaPct}% vs earlier in window`
                    : undefined
                }
                deltaTone={
                  deploymentSpark.deltaPct == null
                    ? 'neutral'
                    : deploymentSpark.deltaPct > 0
                      ? 'positive'
                      : deploymentSpark.deltaPct < 0
                        ? 'negative'
                        : 'neutral'
                }
                value={
                  deploymentFreqPerDay != null ? (
                    <>
                      {deploymentFreqPerDay}
                      <span className="text-sm font-normal text-slate-500">/day</span>
                    </>
                  ) : (
                    '—'
                  )
                }
                accent="purple"
              />
              <KpiCard
                label="Mean lead time"
                icon={Clock}
                sparkline={leadTimeSpark.sparkline}
                delta={
                  leadTimeSpark.deltaPct != null
                    ? `${leadTimeSpark.deltaPct > 0 ? '+' : ''}${leadTimeSpark.deltaPct}% vs earlier in window`
                    : undefined
                }
                deltaTone={
                  leadTimeSpark.deltaPct == null
                    ? 'neutral'
                    : leadTimeSpark.deltaPct < 0
                      ? 'positive'
                      : leadTimeSpark.deltaPct > 0
                        ? 'negative'
                        : 'neutral'
                }
                value={meanLeadTimeHoursLabel ?? '—'}
                accent="teal"
              />
              <KpiCard
                label="Active contributors"
                icon={Users}
                value={activeContributorsValue != null ? activeContributorsValue : '—'}
                accent="purple"
              />
            </div>
          </FeatureGate>

          <FeatureGate flag="ui.dashboard.cycleTimeSection">
            <div className="section-block-mdl">
              <CycleTimeSection className="!mt-0" fetchScope={orgId ? { orgId } : undefined} />
            </div>
          </FeatureGate>

          <div className="section-block-mdl">
            <CommitHeatmap
              scope="organisation"
              organisationId={orgId}
              title="Activity Heatmap"
              fetchEnabled={activeDashboardTab === 'overview' && !!orgId}
            />
          </div>
        </div>
      )}

      {activeDashboardTab === 'reports' && (
        <div
          role="tabpanel"
          id="dashboard-tabpanel-reports"
          aria-labelledby="dashboard-tab-reports"
        >
          <FeatureGate flag="ui.dashboard.doraSection">
            <div className="section-block-mdl card-grid-2-mdl">
              <DeploymentFrequencyDoraCard
                loading={deploymentLoading}
                noData={!deploymentDora}
                dates={deploymentDoraMeta?.mergeDeploymentsByDay?.dates}
                counts={deploymentDoraMeta?.mergeDeploymentsByDay?.counts}
                windowDays={deploymentWindowDays}
                period={deploymentPeriod}
                chartType={doraChartType}
                onPeriodChange={setDeploymentPeriod}
                onChartTypeChange={setDoraChartType}
                emptyMessage="No merges to default branch in this window across assigned repositories. Unassigned repos are excluded. Sync repos to load commits."
              />
              <LeadTimeDoraCard
                loading={leadTimeLoading}
                noData={!leadTimeDora}
                dates={leadTimeDoraMeta?.leadTimeByDeployment?.dates}
                leadTimeMinutes={leadTimeDoraMeta?.leadTimeByDeployment?.leadTimeMinutes}
                meanLeadTimeMinutes={leadTimeDoraMeta?.meanLeadTimeMinutes}
                period={leadTimePeriod}
                chartType={leadTimeChartType}
                onPeriodChange={setLeadTimePeriod}
                onChartTypeChange={setLeadTimeChartType}
                emptyMessage="No merges to default branch in this window across assigned repositories. Unassigned repos are excluded. Sync repos to load commits."
              />
            </div>

            <div className="section-block-mdl card-grid-2-mdl items-stretch">
              <ChurnRatioCard
                className="!mt-0 h-full"
                loading={churnLoading}
                noData={
                  !churnData ||
                  !churnData.dates?.length ||
                  (churnData.ratios?.length > 0 && churnData.ratios.every((r) => r === 0))
                }
                dates={churnData?.dates}
                ratios={churnData?.ratios}
                windowDays={churnWindowDays}
                period={churnPeriod}
                chartType={churnChartType}
                onPeriodChange={setChurnPeriod}
                onChartTypeChange={setChurnChartType}
                emptyMessage="No commit data in this window across assigned repositories."
              />
              <CodeCompositionCard
                className="!mt-0 h-full"
                fetchScope={orgId ? { orgId } : undefined}
              />
            </div>

            <div className="section-block-mdl">
              <CodeCompositionOverTimeCard
                className="!mt-0"
                fetchScope={orgId ? { orgId } : undefined}
              />
            </div>
          </FeatureGate>
        </div>
      )}

      {activeDashboardTab === 'commits' && showCommitsTab && (
        <DashboardCommitsTab orgId={orgId} active={activeDashboardTab === 'commits'} />
      )}

      {activeDashboardTab === 'pull-requests' && showPullRequestsTab && (
        <DashboardPullRequestsTab orgId={orgId} active={activeDashboardTab === 'pull-requests'} />
      )}

      {activeDashboardTab === 'streams' && (
        <div
          role="tabpanel"
          id="dashboard-tabpanel-streams"
          aria-labelledby="dashboard-tab-streams"
        >
          <div className="section-block-mdl">
            <div className="mb-4">
              <SectionTitle>Streams</SectionTitle>
            </div>
            {dashboardStreamsLoading ? (
              <p className="text-sm text-slate-400">Loading streams…</p>
            ) : dashboardStreams.length === 0 ? (
              <EmptyState
                icon={<Waves className="w-12 h-12 text-slate-500" />}
                title="No streams yet."
                description="Add streams from Configuration → System."
              />
            ) : (
              <div className="cards-grid-mdl-dense">
                {dashboardStreams.map((s) => (
                  <OrganisationStreamCard key={s.id} orgId={orgId} stream={s} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeDashboardTab === 'projects' && (
        <div
          role="tabpanel"
          id="dashboard-tabpanel-projects"
          aria-labelledby="dashboard-tab-projects"
        >
          <div className="section-block-mdl">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <SectionTitle>Projects</SectionTitle>
              <div className="w-64 max-w-full min-w-0">
                <SearchBar
                  value={projectsSearch}
                  onChange={setProjectsSearch}
                  placeholder="Search projects…"
                />
              </div>
            </div>
            {dashboardStreams.length === 0 ? (
              <EmptyState
                icon={<FolderKanban className="w-12 h-12 text-slate-500" />}
                title="Create a stream first."
                description="Streams contain projects. Add a stream from the Streams tab."
                action={
                  <button
                    type="button"
                    onClick={() => setActiveDashboardTab('streams')}
                    className="btn-primary-mdl"
                  >
                    Go to Streams tab
                  </button>
                }
              />
            ) : dashboardProjectsLoading ? (
              <p className="text-sm text-slate-400">Loading projects…</p>
            ) : filteredDashboardProjects.length === 0 ? (
              dashboardProjects.length > 0 ? (
                <p className="text-sm text-slate-400">No projects match your search.</p>
              ) : (
                <EmptyState
                  icon={<FolderKanban className="w-12 h-12 text-slate-500" />}
                  title="No projects yet."
                  description="Add projects from Configuration → System."
                />
              )
            ) : (
              <div className="cards-grid-mdl-dense">
                {filteredDashboardProjects.map((p) => (
                  <OrganisationProjectCard
                    key={`${p.streamId}-${p.id}`}
                    orgId={orgId}
                    streamId={p.streamId}
                    project={p}
                    streamLabel={p.streamName}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeDashboardTab === 'repos' && (
        <div role="tabpanel" id="dashboard-tabpanel-repos" aria-labelledby="dashboard-tab-repos">
          <div className="section-block-mdl">
            <RepositoriesConnectedTable
              repositories={filteredDashboardRepos}
              loading={dashboardReposLoading}
              navigate={navigate}
              showSearch
              search={reposSearch}
              onSearchChange={setReposSearch}
              searchPlaceholder="Search Repositories"
              emptyIcon={<GitFork className="w-12 h-12 text-slate-500" />}
              emptyTitle={
                hasReposSearch && dashboardRepos.length > 0
                  ? 'No repositories match your search.'
                  : 'No repositories connected yet.'
              }
              emptyDescription={
                hasReposSearch && dashboardRepos.length > 0
                  ? 'Try a different search term.'
                  : 'Connect repositories from a project or open the full Repositories page.'
              }
              emptyAction={
                !hasReposSearch || dashboardRepos.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => navigate('/repositories')}
                    className="btn-primary-mdl"
                  >
                    Repositories
                  </button>
                ) : undefined
              }
              paginationIdPrefix="dashboard-repos"
            />
          </div>
        </div>
      )}

      {activeDashboardTab === 'contributors' && showContributorsTab && (
        <div
          role="tabpanel"
          id="dashboard-tabpanel-contributors"
          aria-labelledby="dashboard-tab-contributors"
        >
          <FeatureGate flag="ui.dashboard.contributorsBarChart">
            <div className="section-block-mdl">
              <div className="card-grid-4-mdl mb-4">
                <KpiCard
                  label="Total commits"
                  value={contributorsSummary.totalCommits}
                  accent="purple"
                />
                <KpiCard label="Total PRs" value={contributorsSummary.totalPRs} accent="purple" />
                <KpiCard
                  label="Net lines"
                  value={contributorsSummary.netLinesLabel}
                  accent="teal"
                />
                <KpiCard
                  label="Most active"
                  value={contributorsSummary.mostActiveName ?? '—'}
                  accent="purple"
                />
              </div>
              <ContributorsBarChartCard
                contributorRows={filteredContributorsBarRows}
                loading={contributorsBarLoading}
                listKey={orgId ? `org-${orgId}` : 'org'}
                emptyMessage="No contributor data yet. Sync repositories to load commits."
                timeframe={contributorsTimeframe}
                status={contributorsStatus}
                onTimeframeChange={setContributorsTimeframe}
                onStatusChange={setContributorsStatus}
                search={contributorsSearch}
                onSearchChange={setContributorsSearch}
              />
            </div>
          </FeatureGate>
        </div>
      )}

      <AddMemberModal
        isOpen={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        orgId={orgId}
        onSuccess={invalidateStatsOnly}
      />
    </PageContainer>
  );
};

export default Dashboard;
